import * as THREE from 'three';

/**
 * Wish-direction locomotion with acceleration / deceleration.
 */
export class PlayerMovement {
  /**
   * @param {import('../config.js').CONFIG['player']} config
   */
  constructor(config) {
    this.config = config;
    this._forward = new THREE.Vector3();
    this._right = new THREE.Vector3();
    this._wishDir = new THREE.Vector3();
    this._targetVelocity = new THREE.Vector3();
    this._worldUp = new THREE.Vector3(0, 1, 0);
    this._euler = new THREE.Euler(0, 0, 0, 'YXZ');
    this._quat = new THREE.Quaternion();
  }

  /**
   * @param {{
   *   input: import('../game/InputManager.js').InputManager;
   *   lookYaw: number;
   *   lookPitch: number;
   *   onGround: boolean;
   *   velocity: THREE.Vector3;
   *   delta: number;
   * }} ctx
   */
  apply(ctx) {
    const { input, lookYaw, lookPitch, onGround, velocity, delta } = ctx;
    const {
      walkSpeed,
      sprintSpeed,
      jumpForce,
      gravity,
      groundAcceleration,
      groundDeceleration,
      airAcceleration,
    } = this.config;

    const sprinting =
      input.isKeyDown('ShiftLeft') || input.isKeyDown('ShiftRight');
    const maxSpeed = sprinting ? sprintSpeed : walkSpeed;

    this._setPlanarBasis(lookYaw, lookPitch);

    this._wishDir.set(0, 0, 0);
    if (input.isKeyDown('KeyW')) this._wishDir.add(this._forward);
    if (input.isKeyDown('KeyS')) this._wishDir.sub(this._forward);
    if (input.isKeyDown('KeyD')) this._wishDir.add(this._right);
    if (input.isKeyDown('KeyA')) this._wishDir.sub(this._right);
    if (this._wishDir.lengthSq() > 0) this._wishDir.normalize();

    this._targetVelocity.copy(this._wishDir).multiplyScalar(maxSpeed);

    const accelerating = this._wishDir.lengthSq() > 0;
    const accel = onGround ? groundAcceleration : airAcceleration;
    const decel = groundDeceleration;

    velocity.x = approach(
      velocity.x,
      this._targetVelocity.x,
      accelerating ? accel : decel,
      delta,
    );
    velocity.z = approach(
      velocity.z,
      this._targetVelocity.z,
      accelerating ? accel : decel,
      delta,
    );

    if (onGround && input.consumeKey('Space')) {
      velocity.y = jumpForce;
    }

    velocity.y += gravity * delta;

    return {
      horizontalSpeed: Math.hypot(velocity.x, velocity.z),
      sprinting,
    };
  }

  /** Match Three.js camera YXZ orientation on the XZ plane. */
  _setPlanarBasis(yaw, pitch) {
    this._euler.set(pitch, yaw, 0, 'YXZ');
    this._quat.setFromEuler(this._euler);

    this._forward.set(0, 0, -1).applyQuaternion(this._quat);
    this._forward.y = 0;
    if (this._forward.lengthSq() < 1e-10) {
      this._forward.set(0, 0, -1);
    } else {
      this._forward.normalize();
    }

    this._right.set(1, 0, 0).applyQuaternion(this._quat);
    this._right.y = 0;
    if (this._right.lengthSq() < 1e-10) {
      this._right.crossVectors(this._forward, this._worldUp).normalize();
    } else {
      this._right.normalize();
    }
  }
}

function approach(current, target, rate, delta) {
  if (current === target) return target;
  const step = rate * delta;
  if (current < target) return Math.min(current + step, target);
  return Math.max(current - step, target);
}
